// src/navigation/MainTabs.tsx
// Central tab shell: IonTabs + IonTabBar replace the old custom BottomNav.
//
// Split:
//  • Eager imports  → 5 main tab screens (no Suspense, instant load)
//  • Lazy imports   → all detail/utility screens (skeleton fallback on first visit)

import React, { Suspense, lazy } from 'react';
import {
  IonTabs,
  IonRouterOutlet,
  IonTabBar,
  IonTabButton,
  IonLabel,
} from '@ionic/react';
import { Route, Redirect, useLocation } from 'react-router-dom';
import { Home, FileCheck, Sparkles, Users, Shield } from 'lucide-react';

// ── Eager: login + tab root screens — included in main bundle ─────────────────
import LoginScreen       from '../screens/LoginScreen';
import HomeScreen        from '../screens/HomeScreen';
import AIChatScreen      from '../screens/AIChatScreen';
import ProfileScreen     from '../screens/ProfileScreen';
import FamilyScreen      from '../screens/FamilyScreen';
import TransactionsScreen from '../screens/TransactionsScreen';
import PageSkeleton      from '../components/PageSkeleton';

// ── Lazy: detail screens — split-chunked, skeleton shown on first visit ────────
const RetirementScreen   = lazy(() => import('../screens/RetirementScreen'));
const RetirementPlanner  = lazy(() => import('../screens/RetirementPlanner'));
const TakafulScreen      = lazy(() => import('../screens/TakafulScreen'));
const ZakatScreen        = lazy(() => import('../screens/ZakatScreen'));
const HajjScreen         = lazy(() => import('../screens/HajjScreen'));
const LegacyScreen       = lazy(() => import('../screens/LegacyScreen'));
const FaraidScreen       = lazy(() => import('../screens/FaraidScreen'));
const EducationScreen    = lazy(() => import('../screens/EducationScreen'));
const WealthScreen       = lazy(() => import('../screens/WealthScreen'));
const AssetsScreen       = lazy(() => import('../screens/AssetsScreen'));
const LiabilitiesScreen  = lazy(() => import('../screens/LiabilitiesScreen'));
const AssessmentScreen   = lazy(() => import('../screens/AssessmentScreen'));
const SettingsScreen     = lazy(() => import('../screens/SettingsScreen'));
const DiagnosticScreen   = lazy(() => import('../screens/DiagnosticScreen'));

// Wraps a lazy component in its own Suspense boundary with a skeleton fallback.
const lazyScreen = (Screen: React.ComponentType) => () => (
  <Suspense fallback={<PageSkeleton />}>
    <Screen />
  </Suspense>
);

const MainTabs: React.FC = () => {
  const location = useLocation();
  // Hide the tab bar on login and root redirect so no nav chrome shows
  const hideTabBar = location.pathname === '/login' || location.pathname === '/';

  return (
    <IonTabs>
      <IonRouterOutlet>

        {/* ── Auth: login has no tab button, tab bar hidden via hideTabBar ─ */}
        <Route exact path="/login"        render={() => <LoginScreen />} />
        <Route exact path="/"             render={() => <Redirect to="/login" />} />

        {/* ── Eager: zero-spinner tab roots ───────────────────────────────── */}
        <Route exact path="/home"         render={() => <HomeScreen />} />
        <Route exact path="/dashboard"    render={() => <HomeScreen />} />
        <Route exact path="/ai"           render={() => <AIChatScreen />} />
        <Route exact path="/profile"      render={() => <ProfileScreen />} />
        <Route exact path="/family"       render={() => <FamilyScreen />} />
        <Route exact path="/transactions" render={() => <TransactionsScreen />} />
        <Route exact path="/cashflow"     render={() => <TransactionsScreen />} />

        {/* ── Lazy: detail screens with per-screen skeleton ────────────────── */}
        <Route exact path="/retirement"         render={lazyScreen(RetirementScreen)} />
        <Route exact path="/retirement-planner" render={lazyScreen(RetirementPlanner)} />
        <Route exact path="/takaful"            render={lazyScreen(TakafulScreen)} />
        <Route exact path="/takaful/gap"        render={lazyScreen(TakafulScreen)} />
        <Route exact path="/zakat"              render={lazyScreen(ZakatScreen)} />
        <Route exact path="/hajj"               render={lazyScreen(HajjScreen)} />
        <Route exact path="/legacy"             render={lazyScreen(LegacyScreen)} />
        <Route exact path="/faraid"             render={lazyScreen(FaraidScreen)} />
        <Route exact path="/education"          render={lazyScreen(EducationScreen)} />
        <Route exact path="/wealth"             render={lazyScreen(WealthScreen)} />
        <Route exact path="/networth"           render={lazyScreen(WealthScreen)} />
        <Route exact path="/assets"             render={lazyScreen(AssetsScreen)} />
        <Route exact path="/investment"         render={lazyScreen(AssetsScreen)} />
        <Route exact path="/liabilities"        render={lazyScreen(LiabilitiesScreen)} />
        <Route exact path="/assessment"         render={lazyScreen(AssessmentScreen)} />
        <Route exact path="/optimize"           render={lazyScreen(AssessmentScreen)} />
        <Route exact path="/settings"           render={lazyScreen(SettingsScreen)} />
        <Route exact path="/diagnostic"         render={lazyScreen(DiagnosticScreen)} />

        {/* Fallback: unknown routes go to home */}
        <Route><Redirect to="/home" /></Route>

      </IonRouterOutlet>

      {/* ── Native tab bar — hidden on login, active state managed by Ionic ─ */}
      <IonTabBar
        slot="bottom"
        hidden={hideTabBar}
        style={{
          '--background': 'rgba(255,255,255,0.95)',
          '--border': '1px solid #E5E7EB',
          '--color': '#9CA3AF',
          '--color-selected': '#1B5A40',
        } as any}
      >
        <IonTabButton tab="home" href="/home">
          <Home size={22} />
          <IonLabel>Home</IonLabel>
        </IonTabButton>

        <IonTabButton tab="plans" href="/transactions">
          <FileCheck size={22} />
          <IonLabel>Plans</IonLabel>
        </IonTabButton>

        <IonTabButton tab="ai" href="/ai">
          <Sparkles size={22} />
          <IonLabel>AI Advisor</IonLabel>
        </IonTabButton>

        <IonTabButton tab="family" href="/family">
          <Users size={22} />
          <IonLabel>Family</IonLabel>
        </IonTabButton>

        <IonTabButton tab="profile" href="/profile">
          <Shield size={22} />
          <IonLabel>Profile</IonLabel>
        </IonTabButton>
      </IonTabBar>
    </IonTabs>
  );
};

export default MainTabs;
